import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Grid,
  Paper,
  Typography,
  styled,
} from '@mui/material';
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useLocation, useParams} from 'react-router-dom';
import {useAppDispatch, useAppSelector} from '../../store/hooks';
import {getPlayerSessionDetailAsync} from '../../store/playerSessionSlice';
import FullScreenLoadingSpinner from '../shared/FullScreenLoading';
import {Helmet} from 'react-helmet';
import {CurrentMembershipLevels, Roles, SessionTypes} from '../../enums';
import SessionVideoManager from '../shared/SessionVideoManager';
import {DateTime} from 'luxon';
import MembershipLocked from '../shared/MembershipLocked';
import PageTitle from '../shared/PageTitle';
import PageSubtitle from '../shared/PageSubtitle';
import {useTranslation} from 'react-i18next';
import BackButton from '../shared/BackButton';
import {getTime} from '../../services/dateService';
import {getIntensityText} from '../../services/playerSessionService';
import {
  getSpeedMultiplier,
  getComparisonFromBaseline,
  getEnergyRatioFromFloat,
  getEnergyRatio,
} from '../../services/measurementService';
import PlayerSessionShifts from '../shared/PlayerSessionShifts';
import {VictoryAxis} from 'victory';
import PlayerSessionLineChart from '../shared/PlayerSessionLineChart';
import {yAxisStyle} from '../../services/graphService';
import {SizeMe} from 'react-sizeme';
import ExportSessionModal from '../shared/ExportSessionModal';
import SubtitleContainer from '../shared/SubtitleContainer';
import {AvgDataObject, PlayerSessionShift} from '../../interfaces';
import SessionGridItem from '../shared/SessionGridItem';
import SessionVideoPlayer from '../shared/SessionVideoPlayer';
import {downloadAllShifts} from '../../api/playerSessionAPI';
import DownloadSessionJson from '../Admin/DownloadSessionJson';
import PlayerSessionHeader from './PlayerSessionHeader';
import SessionMetaData from '../shared/SessionMetaData';
import DeletePlayerSession from '../Admin/DeletePlayerSession';
import AdminChangePlayerSessionType from '../Admin/AdminChangePlayerSessionType';

const ShiftTitleWrapper = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
}));

const StyledSessionType = styled('span')(() => ({
  display: 'block',
  textTransform: 'capitalize',
}));

interface Props {}

const PlayerSessionDetail: React.FC<Props> = () => {
  const {t} = useTranslation();
  const profilePreferredUnits = useAppSelector(
    state => state.profile?.profile?.preferredUnits,
  );
  const profileRole = useAppSelector(state => state.profile?.profile?.role);
  const profileId = useAppSelector(state => state.profile?.profile?.id);
  const playerSessionStatus = useAppSelector(
    state => state.playerSession?.status,
  );
  const playerSession = useAppSelector(
    state => state.playerSession?.playerSession,
  );
  const playerSessionType = useAppSelector(
    state => state.playerSession?.playerSession?.type,
  );
  const teamSessionGroupId = useAppSelector(
    state =>
      state.playerSession?.playerSession?.teamSession?.teamSessionGroupId,
  );
  const playerSessionStartTime = useAppSelector(
    state => state.playerSession?.playerSession?.startTime,
  );
  const playerSessionEndTime = useAppSelector(
    state => state.playerSession?.playerSession?.endTime,
  );
  const playerSessionLocalDisplayStartTime = useAppSelector(
    state => state.playerSession?.playerSession?.localDisplayStartTime,
  );
  const playerSessionTimeZone = useAppSelector(
    state => state.playerSession?.playerSession?.timeZone,
  );
  const playerSessionVideos = useAppSelector(
    state => state.playerSession?.playerSession?.sessionVideos ?? [],
  );
  const playerSessionTeamSessionVideos = useAppSelector(
    state =>
      state.playerSession?.playerSession?.teamSession?.sessionVideos ?? [],
  );
  const baseline = useAppSelector(
    state => state.playerSession?.playerSession?.baseline,
  );
  const role = useAppSelector(state => state.profile?.profile?.role);
  const [showShiftReview, setShowShiftReview] = useState<boolean>(false);
  const [loadingZip, setLoadingZip] = useState<boolean>(false);
  const dispatch = useAppDispatch();
  const {
    teamId,
    profileId: profileIdParam,
    teamSessionId,
    playerSessionId,
  } = useParams();
  const playerSessionIdInt = useMemo(
    () => parseInt(playerSessionId ?? ''),
    [playerSessionId],
  );
  const teamIdInt = useMemo(() => parseInt(teamId ?? ''), [teamId]);
  const profileIdInt = useMemo(
    () => parseInt(profileIdParam ?? ''),
    [profileIdParam],
  );
  const hash = useLocation().hash;

  const {backPath, backText} = useMemo(() => {
    let backPath = '/';
    let backText = t('navigation.back', {pageName: t('pages.playerSessions')});
    if (
      profileRole === Roles.admin &&
      !teamId &&
      playerSession?.player?.profile?.id
    ) {
      // Admin arrived from the admin view of the player's sessions list.
      backPath = `/admin/profiles/${playerSession.player.profile.id}/playersessions`;
    } else if (profileRole === Roles.parent) {
      backPath = `/family/children/${playerSession?.player?.profile?.id}/playersessions`;
      backText = t('navigation.back', {pageName: t('pages.playerSessions')});
    } else if (teamId && profileIdParam && teamSessionGroupId) {
      backPath = `/playersessions/groups/${teamSessionGroupId}${hash}`;
      backText = t('navigation.back', {
        pageName: t('pages.playerSessionGroupDetail'),
      });
    } else if (teamId && profileIdParam) {
      backPath = `/teams/${teamId}/players/${profileIdParam}/playersessions`;
      backText = t('navigation.back', {pageName: t('pages.playerSessions')});
    } else if (teamId && teamSessionId && teamSessionGroupId) {
      const profileIdForReturn = playerSession?.player?.profile?.id || 0;
      backPath = `/teams/${teamId}/players/${profileIdForReturn}/playersessions/groups/${teamSessionGroupId}${hash}`;
      backText = t('navigation.back', {
        pageName: t('pages.playerSessionGroupDetail'),
      });
    } else if (teamId && teamSessionId) {
      backPath = `/teams/${teamId}/teamsessions/${teamSessionId}`;
      backText = t('navigation.back', {pageName: t('pages.teamSessionDetail')});
    }
    return {backPath, backText};
  }, [
    hash,
    playerSession?.player?.profile?.id,
    profileIdParam,
    profileRole,
    t,
    teamId,
    teamSessionGroupId,
    teamSessionId,
  ]);

  useEffect(() => {
    dispatch(getPlayerSessionDetailAsync(playerSessionIdInt));
  }, [dispatch, playerSessionIdInt]);

  const title = useMemo(
    () =>
      t('navigation.pageTitle', {
        pageName: t('pages.playerSessionDetail'),
      }),
    [t],
  );

  const shiftClips: PlayerSessionShift[] = useMemo(
    () =>
      (playerSession?.playerSessionShifts ?? [])
        .filter(
          (shift: PlayerSessionShift) =>
            shift.valid && (shift.playerSessionVideoClips ?? []).length > 0,
        )
        .map(shift => ({
          ...shift,
          drill: playerSession?.teamSession?.teamSessionSessionEvents?.find(
            event =>
              shift?.endTime &&
              shift?.startTime &&
              event?.startTime &&
              event?.endTime &&
              shift?.endTime >= event?.startTime &&
              shift?.startTime <= event?.endTime,
          )?.drill?.name,
        })),
    [
      playerSession?.playerSessionShifts,
      playerSession?.teamSession?.teamSessionSessionEvents,
    ],
  );

  const maxTime = useMemo(() => {
    const timeData: AvgDataObject[] = [
      ...(playerSession?.speedAvgDataV2 ?? []),
      ...(playerSession?.accelAvgDataV2 ?? []),
      ...(playerSession?.strideRateAvgDataV2 ?? []),
      ...(playerSession?.balanceAvgData ?? []),
      ...(playerSession?.heartRateData ?? []),
    ];
    return timeData.filter(point => point.time).length > 0
      ? (timeData
          .map(point => point.time)
          .sort(function (a, b) {
            return (a ?? 0) - (b ?? 0);
          })[timeData.length - 1] as number)
      : 0;
  }, [
    playerSession?.accelAvgDataV2,
    playerSession?.balanceAvgData,
    playerSession?.heartRateData,
    playerSession?.speedAvgDataV2,
    playerSession?.strideRateAvgDataV2,
  ]);

  const shifts = useMemo(
    () =>
      (playerSession?.playerSessionShifts ?? [])
        .filter(
          (x: PlayerSessionShift) =>
            x.valid || (showShiftReview && x.canUpdateValid),
        )
        .map(shift => ({
          ...shift,
          drill: playerSession?.teamSession?.teamSessionSessionEvents?.find(
            event =>
              shift?.endTime &&
              shift?.startTime &&
              event?.startTime &&
              event?.endTime &&
              shift?.endTime >= event?.startTime &&
              shift?.startTime <= event?.endTime,
          )?.drill?.name,
        })),
    [
      playerSession?.playerSessionShifts,
      playerSession?.teamSession?.teamSessionSessionEvents,
      showShiftReview,
    ],
  );

  const isMetric = useMemo(
    () => profilePreferredUnits === 'metric',
    [profilePreferredUnits],
  );
  const speedMultiplier = useMemo(
    () => getSpeedMultiplier(isMetric),
    [isMetric],
  );
  const speedUnitLabel = useMemo(
    () =>
      isMetric
        ? (t('general.units.kphAbbrev') as string)
        : (t('general.units.mphAbbrev') as string),
    [isMetric, t],
  );

  const canSeeShifts = useMemo(
    () =>
      playerSession?.membershipLevel === CurrentMembershipLevels.elite ||
      playerSession?.membershipLevel === CurrentMembershipLevels.pro ||
      [Roles.admin, Roles.coach, Roles.trainer].includes(profileRole),
    [playerSession?.membershipLevel, profileRole],
  );

  const downloadAllShiftVideos = useCallback(
    async (playerSessionId: number) => {
      setLoadingZip(true);
      const res = await downloadAllShifts(playerSessionId, true);
      const downloadProcessing: boolean =
        res.data?.data?.downloadShiftVideos?.downloadProcessing;
      if (downloadProcessing) {
        window.alert(t('video.processingNotification'));
      } else {
        window.alert(t('video.processingFailed'));
      }
      setLoadingZip(false);
    },
    [t],
  );

  const displayStartTime = useMemo(() => {
    if (playerSessionLocalDisplayStartTime) {
      return `${DateTime.fromFormat(
        playerSessionLocalDisplayStartTime,
        'M/d/yyyy h:mm a',
      ).toFormat('EEEE M/d/yyyy h:mm a')} ${playerSessionTimeZone ?? ''}`;
    }
    if (playerSessionStartTime) {
      return DateTime.fromISO(playerSessionStartTime).toFormat(
        'EEEE M/d/yyyy h:mm a ZZZZ',
      );
    }
    return '';
  }, [
    playerSessionLocalDisplayStartTime,
    playerSessionStartTime,
    playerSessionTimeZone,
  ]);

  if (playerSessionStatus === 'loading' || !playerSession) {
    return (
      <>
        <Helmet>
          <title>{title}</title>
        </Helmet>
        <FullScreenLoadingSpinner />
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>{title}</title>
      </Helmet>
      <Container>
        <PageTitle>{t('pages.playerSessionDetail')}</PageTitle>
        <BackButton navigationUrl={backPath}>{backText}</BackButton>
        {profileId !== playerSession?.player?.profile?.id && (
          <PlayerSessionHeader
            number={playerSession?.player?.number}
            firstName={playerSession?.player?.profile?.firstName}
            lastName={playerSession?.player?.profile?.lastName}
            position={playerSession?.player?.profile?.position}
            profilePictureUrl={
              playerSession?.player?.profile?.profilePictureFileKey
            }
          />
        )}
        {displayStartTime && (
          <SubtitleContainer>
            <PageSubtitle sx={{flexGrow: 1, mr: 2}}>
              <StyledSessionType>
                {(playerSessionType ?? '').toUpperCase()}
              </StyledSessionType>
              {displayStartTime}
            </PageSubtitle>
            <ExportSessionModal
              buttonSx={{mb: 2, alignSelf: 'center'}}
              playerSessionIds={[playerSessionIdInt]}
              playerSessionStartTime={playerSessionStartTime}
            />
          </SubtitleContainer>
        )}
        {playerSessionType === SessionTypes.game && (
          <SessionMetaData
            playerSession={playerSession}
            disableEdit={!!playerSession.teamSessionId}
            playerView
          />
        )}
        {role === Roles.admin && playerSession?.id && (
          <Paper sx={{p: 4, my: 4}}>
            <Typography variant="h3">{t('admin.adminOnly')}</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <DownloadSessionJson
                  fileTitle={`playersession_${playerSession.id}_${DateTime.fromISO(
                    playerSessionStartTime ?? '',
                  ).toFormat('yyyy-MM-dd-HH-mm')}`}
                  playerSessionIds={[playerSession.id]}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <DeletePlayerSession playerSessionId={playerSession.id} />
              </Grid>
              {!playerSession.teamSession && (
                <Grid item xs={12} md={4}>
                  <AdminChangePlayerSessionType
                    playerSessionId={playerSession.id}
                    sessionVideoId={
                      playerSession?.sessionVideos?.[0]?.id as number
                    }
                    type={playerSessionType as SessionTypes}
                    onSuccess={() =>
                      dispatch(getPlayerSessionDetailAsync(playerSessionIdInt))
                    }
                  />
                </Grid>
              )}
            </Grid>
          </Paper>
        )}
        <PageSubtitle>{t('video.instantVideoTitle')}</PageSubtitle>
        {playerSession?.membershipLevel === CurrentMembershipLevels.pro ? (
          <>
            {playerSession?.id &&
              (playerSession?.playerSessionShifts ?? []).some(
                playerSessionShift =>
                  (playerSessionShift?.playerSessionVideoClips ?? []).length >
                  0,
              ) && (
                <Box sx={{display: 'flex', alignItems: 'center', mb: 2}}>
                  <Button
                    variant="contained"
                    disabled={loadingZip}
                    onClick={() =>
                      downloadAllShiftVideos(playerSession?.id as number)
                    }>
                    {t('video.downloadAllClips')}
                  </Button>
                  {loadingZip && (
                    <Box sx={{display: 'flex', alignItems: 'center', ml: 2}}>
                      <Typography sx={{mr: 1}}>
                        {t('video.generatingZip')}
                      </Typography>
                      <CircularProgress />
                    </Box>
                  )}
                </Box>
              )}
            {/* if user is player or parent then they can manage session videos */}
            {!playerSession?.teamSessionId &&
            [
              Roles.player,
              Roles.pro,
              Roles.parent,
              Roles.coach,
              Roles.admin,
            ].includes(profileRole) ? (
              <SessionVideoManager
                sessionVideos={playerSessionVideos ?? []}
                sessionType="player"
                teamId={teamIdInt}
                profileId={profileIdInt}
                sessionId={playerSessionIdInt}
                sessionStartTime={playerSessionStartTime}
                sessionEndTime={playerSessionEndTime}
              />
            ) : (
              <Alert severity="info" variant="outlined" sx={{mb: 2}}>
                {t('video.unableToManageTeamSessionClipsAsPlayer')}
              </Alert>
            )}
            {shiftClips.length > 0 && (
              <SessionVideoPlayer
                shiftClips={shiftClips}
                sessionType={playerSessionType}
                sessionVideoSegments={
                  playerSessionVideos?.[0]?.sessionVideoSegments ??
                  playerSessionTeamSessionVideos?.[0]?.sessionVideoSegments ??
                  []
                }
              />
            )}
          </>
        ) : (
          <MembershipLocked minMembershipLevel={CurrentMembershipLevels.pro} />
        )}
        {/* shifts / drill runs */}
        {canSeeShifts ? (
          <>
            <ShiftTitleWrapper>
              <PageSubtitle>
                {playerSessionType === SessionTypes.game
                  ? t('shifts.title')
                  : t('shifts.drillRuns')}
              </PageSubtitle>
              <Box>
                <Button
                  variant="outlined"
                  onClick={() => setShowShiftReview(!showShiftReview)}>
                  {t('shifts.shiftReview')}
                </Button>
              </Box>
            </ShiftTitleWrapper>
            <PlayerSessionShifts
              playerSessionIds={[playerSessionIdInt]}
              type={playerSessionType ?? ''}
              shifts={shifts}
              inReview={showShiftReview}
              setInReview={setShowShiftReview}
              isProcessingVideo={!!playerSession?.videoProcessingStartedAt}
              baseline={playerSession?.baseline}
            />
          </>
        ) : (
          <>
            <PageSubtitle>{t('shifts.title')}</PageSubtitle>
            <MembershipLocked
              minMembershipLevel={CurrentMembershipLevels.elite}
            />
          </>
        )}
        {/* standard session stats */}
        <Paper sx={{mt: 5, mb: 5}}>
          <Grid container spacing={2}>
            <SessionGridItem
              label={t('sessionStats.strides')}
              value={
                (playerSession?.strideCountLeft ?? 0) +
                (playerSession?.strideCountRight ?? 0)
              }
            />
            <SessionGridItem
              label={
                playerSession.type === SessionTypes.game
                  ? t('sessionStats.timeOnIce')
                  : t('sessionStats.activeTime')
              }
              value={
                getTime(
                  playerSession.type === SessionTypes.game
                    ? playerSession.timeOnIce
                    : playerSession.activeTime,
                ) ?? '--'
              }
            />
            <SessionGridItem
              label={t('sessionStats.strideTime')}
              value={getTime(playerSession.strintTotalTime) ?? '--'}
            />
            <SessionGridItem
              label={t('sessionStats.agilityAvg')}
              value={
                playerSession.strintFilteredStrideRateAvgV2
                  ? playerSession.strintFilteredStrideRateAvgV2.toFixed(0)
                  : '--'
              }
              comparison={getComparisonFromBaseline({
                stat: playerSession.strintFilteredStrideRateAvgV2,
                baseline: baseline?.strintFilteredStrideRateAvgV2,
              })}
              unit={'spm'}
            />
            <SessionGridItem
              label={t('sessionStats.agilityMax')}
              value={
                playerSession.strintFilteredStrideRateMaxV2
                  ? playerSession.strintFilteredStrideRateMaxV2.toFixed(0)
                  : '--'
              }
              comparison={getComparisonFromBaseline({
                stat: playerSession.strintFilteredStrideRateMaxV2,
                baseline: baseline?.strintFilteredStrideRateMaxV2,
              })}
              unit={'spm'}
            />
            <SessionGridItem
              label={t('sessionStats.points')}
              value={playerSession.points ?? '--'}
            />
            {playerSession.hustleScoreTopLevel ? (
              <SessionGridItem
                label={t('sessionStats.hustle')}
                value={Math.round(playerSession.hustleScoreTopLevel)}
                comparison={getComparisonFromBaseline({
                  stat: playerSession.hustleScoreTopLevel,
                  baseline: baseline?.hustleScoreTopLevel,
                })}
              />
            ) : (
              <SessionGridItem
                label={t('sessionStats.hustle')}
                value={
                  playerSession.hustleScore
                    ? getIntensityText(playerSession.hustleScore).toUpperCase()
                    : '--'
                }
              />
            )}
            <SessionGridItem
              label={t('sessionStats.strideSpeedAvg')}
              value={
                playerSession.speedAvgV2
                  ? (playerSession.speedAvgV2 * speedMultiplier).toFixed(1)
                  : '--'
              }
              comparison={getComparisonFromBaseline({
                stat: playerSession.speedAvgV2,
                baseline: baseline?.speedAvgV2,
              })}
              unit={speedUnitLabel}
            />
            <SessionGridItem
              label={t('sessionStats.strideSpeedMax')}
              value={
                playerSession.speedMaxV2
                  ? (playerSession.speedMaxV2 * speedMultiplier).toFixed(1)
                  : '--'
              }
              comparison={getComparisonFromBaseline({
                stat: playerSession.speedMaxV2,
                baseline: baseline?.speedMaxV2,
              })}
              unit={speedUnitLabel}
            />
            <SessionGridItem
              label={t('sessionStats.explosivenessAvg')}
              value={
                playerSession.accelAvgV2
                  ? (playerSession.accelAvgV2 / 9.8).toFixed(2)
                  : '--'
              }
              comparison={getComparisonFromBaseline({
                stat: playerSession.accelAvgV2,
                baseline: baseline?.accelAvgV2,
              })}
              unit={t('general.units.gAbbrev') as string}
            />
            <SessionGridItem
              label={t('sessionStats.explosivenessMax')}
              value={
                playerSession.accelMaxV2
                  ? (playerSession.accelMaxV2 / 9.8).toFixed(2)
                  : '--'
              }
              comparison={getComparisonFromBaseline({
                stat: playerSession.accelMaxV2,
                baseline: baseline?.accelMaxV2,
              })}
              unit={t('general.units.gAbbrev') as string}
            />
            <SessionGridItem
              label={t('sessionStats.balancePercent')}
              value={
                playerSession.balance
                  ? `${Math.round(playerSession.balance * 100)} / ${Math.round((1 - playerSession.balance) * 100)}`
                  : '--'
              }
              comparison={getComparisonFromBaseline({
                stat: playerSession.balanceNormalized,
                baseline: baseline?.balanceNormalized,
              })}
            />
            <SessionGridItem
              label={t('sessionStats.energyRatio')}
              value={
                getEnergyRatioFromFloat(
                  getEnergyRatio({
                    ratio: playerSession.energyRatio,
                  }),
                  true,
                ) ?? '--'
              }
              unit={t('general.units.percentSymbol') as string}
              comparison={getComparisonFromBaseline({
                stat: getEnergyRatio({
                  ratio: playerSession.energyRatio,
                }),
                baseline: baseline?.energyRatio,
              })}
            />
            {/* TODO, this item should show lock if user is not elite or pro */}
            <SessionGridItem
              label={t('sessionStats.heartRateAvg')}
              value={playerSession.hrAvg ?? '--'}
              unit={t('general.units.bpmAbbrev') as string}
              comparison={getComparisonFromBaseline({
                stat: playerSession.hrAvg,
                baseline: baseline?.hrAvg,
              })}
            />
          </Grid>
          {(playerSession?.speedAvgDataV2 ?? []).length > 0 && (
            <Box sx={{m: 5}}>
              <Typography variant="h4">
                {t('statistics.strideSpeedDetail')}
              </Typography>
              <PlayerSessionLineChart
                data={(playerSession?.speedAvgDataV2 ?? []).map(point => ({
                  x: point.time,
                  y: (point.value ?? 0) * speedMultiplier,
                }))}
                metric={` ${speedUnitLabel}`}
                maxTime={maxTime}
              />
            </Box>
          )}
          {(playerSession?.accelAvgDataV2 ?? []).length > 0 && (
            <Box sx={{m: 5}}>
              <Typography variant="h4">
                {t('statistics.explosivenessDetail')}
              </Typography>
              <PlayerSessionLineChart
                data={(playerSession?.accelAvgDataV2 ?? []).map(point => ({
                  x: point.time,
                  y: (point.value ?? 0) / 9.8,
                }))}
                metric={` ${t('general.units.gAbbrev') as string}`}
                maxTime={maxTime}
              />
            </Box>
          )}
          {(playerSession?.strideRateAvgDataV2 ?? []).length > 0 && (
            <Box sx={{m: 5}}>
              <Typography variant="h4">
                {t('statistics.strideRateDetail')}
              </Typography>
              <PlayerSessionLineChart
                data={(playerSession?.strideRateAvgDataV2 ?? []).map(point => ({
                  x: point.time,
                  y: point.value,
                }))}
                metric={` ${t('general.units.spmAbbrev') as string}`}
                maxTime={maxTime}
              />
            </Box>
          )}
          {(playerSession?.balanceAvgData ?? []).length > 0 && (
            <Box sx={{m: 5}}>
              <Typography variant="h4">
                {t('statistics.balanceDetail')}
              </Typography>
              <Box>
                <SizeMe>
                  {({size}) => (
                    <PlayerSessionLineChart
                      data={(playerSession?.balanceAvgData ?? []).map(
                        point => ({
                          x: point.time,
                          y: point.value,
                        }),
                      )}
                      yAxisOverride={
                        <VictoryAxis
                          dependentAxis
                          tickValues={[0.05, 0.5, 0.95]}
                          tickFormat={tick => {
                            if (tick === 0.05) {
                              return 'Right';
                            } else if (tick === 0.5) {
                              return 'Balanced';
                            } else if (tick === 0.95) {
                              return 'Left';
                            }

                            return '';
                          }}
                          orientation="right"
                          style={yAxisStyle}
                          offsetX={size.width ?? 0}
                        />
                      }
                      metric={''}
                      maxTime={maxTime}
                    />
                  )}
                </SizeMe>
              </Box>
            </Box>
          )}
          {(playerSession?.heartRateData ?? []).length > 0 && (
            <Box sx={{m: 5}}>
              <Typography variant="h4">
                {t('statistics.hrAvgDetail')}
              </Typography>
              {playerSession?.membershipLevel ===
                CurrentMembershipLevels.elite ||
              playerSession?.membershipLevel === CurrentMembershipLevels.pro ? (
                <PlayerSessionLineChart
                  data={(playerSession?.heartRateData ?? []).map(point => ({
                    x: point.time,
                    y: point.value1,
                  }))}
                  metric={` ${t('general.units.bpmAbbrev') as string}`}
                  maxTime={maxTime}
                />
              ) : (
                <MembershipLocked
                  minMembershipLevel={CurrentMembershipLevels.elite}
                />
              )}
            </Box>
          )}
        </Paper>
      </Container>
    </>
  );
};

export default PlayerSessionDetail;
